import { cloneElement, createContext, isValidElement, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { t, errorText, money, chips } from '../i18n/index.ts';
import type { Card } from '../../shared/cards.ts';

// ---- Icons (inline SVG, stroke-based) -------------------------------------

const paths: Record<string, ReactNode> = {
  home: <path d="M4 11 12 4l8 7v9a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z" />,
  trophy: <path d="M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8 20h8M10 17h4" />,
  ledger: <path d="M6 3h11a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6zM6 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2M9 8h6M9 12h6M9 16h4" />,
  chat: <path d="M4 5h16v11H9l-5 4z" />,
  bell: <path d="M6 16V11a6 6 0 1 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0" />,
  user: <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0" />,
  shield: <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" />,
  logout: <path d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10" />,
  mic: <path d="M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM6 11a6 6 0 0 0 12 0M12 17v4" />,
  play: <path d="M8 5v14l11-7z" />,
  pause: <path d="M8 5v14M16 5v14" />,
  send: <path d="M4 12 20 4l-6 16-3-7z" />,
  trash: <path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" />,
  clock: <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2" />,
  back: <path d="M15 5l-7 7 7 7" />,
  chart: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  expand: <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
  collapse: <path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" />,
  panel: <path d="M4 5h16v14H4zM15 5v14" />,
  more: <path d="M6 9l6 6 6-6" />,
  users: <path d="M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20a6.5 6.5 0 0 1 13 0M16 4.3a3.5 3.5 0 0 1 0 6.4M18 14a6.5 6.5 0 0 1 3.5 6" />,
  mail: <path d="M3 6h18v12H3zM3 7l9 6 9-6" />,
  stop: <path d="M7 7h10v10H7z" />,
  image: <path d="M4 5h16v14H4zM4 16l5-5 4 4 2-2 5 5M15.5 9.5a1 1 0 1 0 0-.01" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
};

export function Icon({ name, label }: { name: keyof typeof paths | string; label?: string }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden={label ? undefined : true} role={label ? 'img' : undefined} aria-label={label}>
      {paths[name]}
    </svg>
  );
}

// ---- Avatar ------------------------------------------------------------------

export function Avatar({ name, url, size }: { name: string; url: string | null; size?: 's' | 'l' }) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const hue = [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
  return (
    <span className={`avatar ${size ? `avatar-${size}` : ''}`} style={url ? undefined : { background: `hsl(${hue} 35% 38%)` }} aria-hidden="true">
      {url ? <img src={url} alt="" loading="lazy" /> : initials}
    </span>
  );
}

// ---- Playing cards -----------------------------------------------------------

const SUIT_SYMBOL: Record<string, string> = { s: '♠', h: '♥', d: '♦', c: '♣' };
const RANK_TEXT: Record<string, string> = { T: '10' };

export function cardLabel(card: Card): string {
  const rank = card[0]!;
  const suit = card[1]!;
  const rankName = ({ A: t('ranks.A'), K: t('ranks.K'), Q: t('ranks.Q'), J: t('ranks.J'), T: t('ranks.T') } as Record<string, string>)[rank] ?? rank;
  return `${t(`suits.${suit as 's'}`)} ${rankName}`;
}

export function PlayingCard({ card, size, highlight, dim }: { card: Card | null; size?: 's' | 'm' | 'l'; highlight?: boolean; dim?: boolean }) {
  const cls = `pcard ${size ? `pcard-${size}` : ''} ${highlight ? 'win' : ''} ${dim ? 'dim' : ''}`;
  if (!card) return <span className={`${cls} back`} role="img" aria-label={t('table.hidden')} />;
  const suit = card[1]!;
  return (
    <span className={`${cls} ${suit}`} role="img" aria-label={cardLabel(card)}>
      <span className="rank" aria-hidden="true">{RANK_TEXT[card[0]!] ?? card[0]}</span>
      <span className="suit" aria-hidden="true">{SUIT_SYMBOL[suit]}</span>
    </span>
  );
}

export function EmptyCard({ size }: { size?: 's' | 'm' | 'l' }) {
  return <span className={`pcard empty ${size ? `pcard-${size}` : ''}`} aria-hidden="true" />;
}

/** Card rendered inline in text, e.g. in the hand log. */
export function InlineCards({ cards }: { cards: Card[] }) {
  return (
    <>
      {cards.map((c, i) => (
        <span key={i} className={`card-inline ${c[1]}`} role="img" aria-label={cardLabel(c)}>
          {`${RANK_TEXT[c[0]!] ?? c[0]}${SUIT_SYMBOL[c[1]!] ?? ''}`}
          {i < cards.length - 1 ? ' ' : ''}
        </span>
      ))}
    </>
  );
}

export const Money = ({ cents, signed }: { cents: number; signed?: boolean }) => (
  <span className={`num ${signed ? (cents > 0 ? 'positive' : cents < 0 ? 'negative' : '') : ''}`}>
    {signed && cents > 0 ? '+' : ''}
    {money(cents)}
  </span>
);

export const Chips = ({ n }: { n: number }) => <span className="num">{chips(n)}</span>;

export function Status({ kind, children }: { kind: string; children: ReactNode }) {
  return <span className={`status status-${kind}`}>{children}</span>;
}

// ---- Loading / error ------------------------------------------------------------

export function Loading() {
  return (
    <p role="status" className="hint">
      {t('app.loading')}
    </p>
  );
}

export function ErrorView({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="alert alert-error" role="alert">
      <p style={{ margin: 0 }}>{errorText(error)}</p>
      {onRetry && (
        <button className="btn btn-small" style={{ marginTop: 10 }} onClick={onRetry}>
          {t('app.retry')}
        </button>
      )}
    </div>
  );
}

// ---- Dialog ------------------------------------------------------------------------

export function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  // Callers pass a new onClose on every render; the focus setup must run only once.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const focusable = ref.current?.querySelector<HTMLElement>('input, textarea, select, button:not([data-close])');
    (focusable ?? ref.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      if (e.key === 'Tab' && ref.current) {
        const items = [...ref.current.querySelectorAll<HTMLElement>('button, input, textarea, select, a[href]')].filter((el) => !el.hasAttribute('disabled'));
        if (items.length === 0) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, []);
  return (
    <motion.div
      className="dialog-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        ref={ref}
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 420, damping: 32 }}
      >
        <h2 id={id}>{title}</h2>
        {children}
      </motion.div>
    </motion.div>
  );
}

// ---- Toasts --------------------------------------------------------------------------

interface ToastItem {
  id: number;
  text: string;
  kind: 'info' | 'error';
}
const ToastContext = createContext<(text: string, kind?: 'info' | 'error') => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((text: string, kind: 'info' | 'error' = 'info') => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, text, kind }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), kind === 'error' ? 8000 : 4500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite" role="status">
        <AnimatePresence initial={false}>
          {items.map((i) => (
            <motion.div
              key={i.id}
              layout
              className={`toast ${i.kind === 'error' ? 'toast-error' : ''}`}
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 24, transition: { duration: 0.16 } }}
              transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            >
              {i.text}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// ---- Time ------------------------------------------------------------------------------

/**
 * Current server-aligned time, ticking every `intervalMs`. The offset (server clock minus this
 * device's clock) must be computed once per response, e.g. `serverTime - dataUpdatedAt`; it is
 * read through a ref so a new render never restarts the clock.
 */
export function useNow(offsetMs = 0, intervalMs = 1000): number {
  const offset = useRef(offsetMs);
  offset.current = offsetMs;
  const [now, setNow] = useState(() => Date.now() + offsetMs);
  useEffect(() => {
    setNow(Date.now() + offset.current);
    const id = setInterval(() => setNow(Date.now() + offset.current), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string; error?: string | null; children: ReactNode; htmlFor: string }) {
  // The hint (e.g. password rules) is announced together with the field.
  const field =
    hint && isValidElement<{ 'aria-describedby'?: string }>(children)
      ? cloneElement(children, { 'aria-describedby': [children.props['aria-describedby'], `${htmlFor}-hint`].filter(Boolean).join(' ') })
      : children;
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {field}
      {hint && <span className="hint" id={`${htmlFor}-hint`}>{hint}</span>}
      {error && (
        <span className="error-text" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
